import { Button } from "@repo/shad/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@repo/shad/ui/card"
import * as Sentry from "@sentry/react"
import type { ErrorComponentProps } from "@tanstack/react-router"
import { AlertCircle, Home, RefreshCw } from "lucide-react"
import { useRef } from "react"

import { ConnectionErrorFullScreen } from "./connection-error"

function isConvexError(error: unknown): error is {
  name: string
  data: string
} {
  return (
    error !== null &&
    typeof error === "object" &&
    "name" in error &&
    "data" in error &&
    (error as { name: string }).name === "ConvexError"
  )
}

const CONNECTION_ERROR_PATTERNS = [
  "ConvexUnavailableError",
  "Failed to fetch",
  "NetworkError",
  "Load failed",
  "net::ERR_",
]

function isConnectionError(error: unknown): boolean {
  if (error instanceof Error) {
    return CONNECTION_ERROR_PATTERNS.some(
      (pattern) => error.name === pattern || error.message.includes(pattern),
    )
  }
  return false
}

export function RouteError({ error }: ErrorComponentProps) {
  const reportedRef = useRef<unknown>(null)
  if (error && reportedRef.current !== error) {
    reportedRef.current = error
    Sentry.captureException(error)
  }

  if (isConnectionError(error)) {
    return <ConnectionErrorFullScreen />
  }

  const errorMessage = isConvexError(error)
    ? error.data
    : error instanceof Error
      ? error.message
      : "An unexpected error occurred"
  const errorStack = error instanceof Error && error.stack ? error.stack : null

  const handleRefresh = () => {
    window.location.reload()
  }

  const handleGoHome = () => {
    window.location.href = "/"
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-red-500" />
            <CardTitle>Oops! Something went wrong</CardTitle>
          </div>
          <CardDescription>
            We encountered an error while loading this page. Please try again.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className=" p-4 rounded-lg bg-background">
            <p className="text-sm text-red-700 dark:text-red-300">{errorMessage}</p>
          </div>

          {errorStack && (
            <details className="cursor-pointer">
              <summary className="text-sm font-medium text-muted-foreground ">
                Show technical details
              </summary>
              <pre className="mt-2 p-4  bg-background rounded-lg text-xs overflow-x-auto">
                {errorStack}
              </pre>
            </details>
          )}
        </CardContent>
        <CardFooter className="flex gap-2">
          <Button onClick={handleGoHome} variant="default">
            <Home className="mr-2 h-4 w-4" />
            Go to Homepage
          </Button>
          <Button onClick={handleRefresh} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh Page
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
